import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  styled,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'; // Import necessary framer-motion hooks
import { useNavigate } from 'react-router-dom';

import { auth } from '../firebase/config';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';

const db = getFirestore();

// --- Enhanced Styled Components ---
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative', // Crucial for absolute positioning of background layers
  overflow: 'hidden', // Prevent background layers from overflowing
  background: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f0f8ff', // Slightly different base background
}));

// Parallax Background Layers
const BackgroundLayer = styled(motion.div)(({ theme, depth, imageUrl }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '120%', // Slightly larger to prevent gaps during movement
  height: '120%',
  backgroundImage: `url(${imageUrl})`, // Use a prop for the image
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  zIndex: -1 - depth, // Deeper layers go further back
  opacity: 0.1 + (0.1 * (3 - depth)), // Adjust opacity per layer
  filter: `blur(${depth * 0.5}px)`, // Blur background layers slightly
}));

// Floating Particles
const Particle = styled(motion.div)(({ theme, size }) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(67, 233, 123, 0.3)' : 'rgba(33, 150, 243, 0.3)', // Neon green/blue
  borderRadius: '50%',
  zIndex: -1,
}));

const StyledPaper = styled(motion(Paper))(({ theme }) => ({ // Wrap Paper with motion
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: 400,
  borderRadius: 24,
  // Enhanced glassmorphism effect
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.7)' // Darker semi-transparent background
    : 'rgba(255, 255, 255, 0.7)', // Lighter semi-transparent background
  backdropFilter: 'blur(10px)', // Blur effect
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.5), inset 0 0 10px rgba(255, 255, 255, 0.05)'
    : '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 0 10px rgba(255, 255, 255, 0.5)',
}));

const StyledAvatar = styled(motion(Avatar))(({ theme }) => ({ // Wrap Avatar with motion
  margin: '16px auto',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, // Gradient background
  width: 80,
  height: 80,
  fontSize: 40,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 0 20px rgba(67, 233, 123, 0.5)'
    : '0 0 20px rgba(33, 150, 243, 0.5)', // Glow effect
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    // Add glow effect on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.mode === 'dark' ? '#43E97B' : '#2196F3',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 0 10px rgba(67, 233, 123, 0.5)'
        : '0 0 10px rgba(33, 150, 243, 0.5)',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.mode === 'dark' ? '#43E97B' : '#2196F3',
  }
}));

const StyledButton = styled(motion(Button))(({ theme }) => ({ // Wrap Button with motion
  marginTop: theme.spacing(3),
  height: 50,
  borderRadius: 12,
  fontWeight: 'bold',
  textTransform: 'none',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 15px rgba(67, 233, 123, 0.3)'
    : '0 4px 15px rgba(33, 150, 243, 0.3)',
  // Hover effect
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 6px 20px rgba(67, 233, 123, 0.5)'
      : '0 6px 20px rgba(33, 150, 243, 0.5)',
  },
  // Active/Pressed effect
  '&:active': {
    transform: 'scale(0.98)',
  }
}));

// --- Main Component ---
const Login = ({ onLogin }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [input, setInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // --- Parallax Logic ---
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Depth for parallax layers (adjust for desired effect)
  const depth1 = useTransform(x, [0, window.innerWidth], [0, -50]); // Slowest
  const depth2 = useTransform(x, [0, window.innerWidth], [0, -30]);
  const depth3 = useTransform(x, [0, window.innerWidth], [0, -10]); // Fastest (foreground)

  // Mouse move handler for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Normalize mouse position to -0.5 to 0.5 range
      const normalizedX = (e.clientX / window.innerWidth) - 0.5;
      const normalizedY = (e.clientY / window.innerHeight) - 0.5;

      // Update motion values (adjust multiplier for sensitivity)
      x.set(normalizedX * window.innerWidth * 0.1);
      y.set(normalizedY * window.innerHeight * 0.1);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, y]);

  // --- Floating Particles Logic ---
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      const particleCount = isMobile ? 15 : 30; // Fewer particles on mobile
      for (let i = 0; i < particleCount; i++) {
        newParticles.push({
          id: i,
          size: Math.random() * 10 + 2, // Random size between 2 and 12px
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          duration: Math.random() * 20 + 10, // Random duration 10-30s
          delay: Math.random() * 5, // Random start delay
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
  }, [isMobile]);

  const handleTogglePassword = () => setShowPassword(!showPassword);

  // Helper: Check if input is email
  const isEmail = (val) => /.+@.+\..+/.test(val);

  // Helper: Check if input is phone (simple, adjust as needed)
  const isPhone = (val) => /^\+?\d{10,15}$/.test(val);

  // --- LOGIN HANDLER ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    if (!input || !password) {
      setError('Please enter username/email/phone and password.');
      return;
    }
    setLoading(true);
    try {
      let emailToUse = null;
      if (isEmail(input)) {
        emailToUse = input;
      } else {
        // Lookup by username or phone in Firestore
        let q;
        if (isPhone(input)) {
          q = query(collection(db, 'Users'), where('phoneNumber', '==', input));
        } else {
          q = query(collection(db, 'Users'), where('username', '==', input));
        }
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          setError('No user found with provided username/phone.');
          setLoading(false);
          return;
        }
        const userData = querySnapshot.docs[0].data();
        emailToUse = userData.email;
        if (!emailToUse) {
          setError('User email not found in database.');
          setLoading(false);
          return;
        }
      }
      // Firebase Auth login
      await signInWithEmailAndPassword(auth, emailToUse, password);
      // Fetch user data from Firestore
      const userQ = query(collection(db, 'Users'), where('email', '==', emailToUse));
      const userSnap = await getDocs(userQ);
      if (userSnap.empty) {
        setError('User data not found. Please contact support.');
        setLoading(false);
        return;
      }
      const userData = userSnap.docs[0].data();
      const username = userData.username;
      const updateHomeId = userData.updateHomeId;
      const devices = userData.devices;
      if (!devices || devices.length === 0) {
        setError('No devices linked to this account.');
        setLoading(false);
        return;
      }
      const defaultDevice = devices[0];
      const deviceId = defaultDevice.deviceId;
      const originalHomeId = defaultDevice.originalHomeId;
      localStorage.setItem('username', username);
      localStorage.setItem('deviceId', deviceId);
      localStorage.setItem('originalHomeId', originalHomeId);
      localStorage.setItem('updateHomeId', updateHomeId);
      if (onLogin) onLogin();
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No user found with this email.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Invalid password.');
      } else {
        setError('Login failed. ' + (err.message || ''));
      }
    } finally {
      setLoading(false);
    }
  };

  // --- FORGOT PASSWORD HANDLER ---
  const handleForgotPassword = async () => {
    setError('');
    setResetMessage('');
    if (!input) {
      setError('Please enter your email address first.');
      return;
    }
    if (!isEmail(input)) {
      setError('Please enter a valid email address for password reset.');
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, input);
      setResetMessage('Password reset email sent to ' + input);
    } catch (err) {
      setError('Failed to send reset email: ' + (err.message || ''));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <StyledContainer>
      <CssBaseline />
      {/* --- Parallax Background Layers ---
           Replace '/path/to/your/background1.jpg' etc. with actual image paths or remove if using gradients.
           You can also use CSS gradients instead of images. */}
      {/* <BackgroundLayer depth={1} imageUrl="/path/to/your/background1.jpg" style={{ x: depth1, y }} />
      <BackgroundLayer depth={2} imageUrl="/path/to/your/background2.jpg" style={{ x: depth2, y }} />
      <BackgroundLayer depth={3} imageUrl="/path/to/your/background3.jpg" style={{ x: depth3, y }} /> */}

      {/* --- Floating Particles --- */}
      {particles.map((particle) => (
        <Particle
          key={particle.id}
          size={particle.size}
          style={{
            left: particle.left,
            top: particle.top,
          }}
          animate={{
            y: [0, -(Math.random() * 100 + 50)], // Float upwards
            opacity: [0, 1, 0], // Fade in and out
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut",
          }}
        />
      ))}

      {/* --- Login Form --- */}
      <StyledPaper
        elevation={4}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <StyledAvatar
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <LockIcon />
          </StyledAvatar>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Typography component="h1" variant="h5">Sign in</Typography>
          </motion.div>
          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>
            </motion.div>
          )}
          {resetMessage && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Alert severity="success" sx={{ mt: 2, width: '100%' }}>{resetMessage}</Alert>
            </motion.div>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <StyledTextField
                label="Username / Email / Phone"
                fullWidth
                margin="normal"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  )
                }}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <StyledTextField
                label="Password"
                fullWidth
                margin="normal"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </motion.div>
            <Grid container justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }}>
              <Grid item>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  sx={{ textTransform: 'none', color: theme.palette.primary.main }}
                >
                  Forgot Password?
                </Button>
              </Grid>
            </Grid>
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </StyledButton>
          </Box>
        </Box>
      </StyledPaper>
    </StyledContainer>
  );
};

export default Login;